// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mvpApi } from "../services/api";
import {
  clearApplicantAuthSession,
  hasApplicantTabSession,
  isApplicantSessionIdleExpired,
  markApplicantSessionStarted,
} from "../utils/applicantSession";
import { LanguageProvider } from "./context/LanguageContext";
import { FormProvider } from "./context/FormContext";

/** Set when signup phone check finds an existing user; allows unauthenticated Welcome. */
const EXISTING_USER_WELCOME_KEY = "mvp_welcome_existing_user";
const DEFAULT_LOCALE = "es";
const localizedPath = (path: string): string => `/${DEFAULT_LOCALE}${path}`;

/**
 * Auth gate + persistent providers for /intake and /organizer so navigating between them
 * does not remount FormProvider (form state is preserved).
 */
export default function ApplicantPortalRoot() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      if (sessionStorage.getItem(EXISTING_USER_WELCOME_KEY) === "1") {
        setReady(true);
        return;
      }
      navigate(localizedPath("/login"), { replace: true });
      return;
    }
    if (!hasApplicantTabSession() || isApplicantSessionIdleExpired()) {
      clearApplicantAuthSession();
      navigate(localizedPath("/login"), { replace: true });
      return;
    }
    markApplicantSessionStarted();
    sessionStorage.removeItem(EXISTING_USER_WELCOME_KEY);
    mvpApi
      .getMe()
      .then((res) => {
        const user = res.data?.user || res.data;
        const role = user?.role?.value ?? user?.role;
        if (role === "admin") {
          navigate(localizedPath("/advisor"), { replace: true });
          return;
        }
        setReady(true);
      })
      .catch(() => {
        clearApplicantAuthSession();
        navigate(localizedPath("/login"), { replace: true });
      });
  }, [navigate]);

  const clearExistingUserWelcomeFlag = () => {
    sessionStorage.removeItem(EXISTING_USER_WELCOME_KEY);
  };

  if (!ready) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-[#F8F7F4] flex flex-col items-center justify-center">
        <p className="text-slate-500 text-sm">{t("signingIn")}</p>
      </div>
    );
  }

  clearExistingUserWelcomeFlag();
  return (
    <LanguageProvider>
      <FormProvider>
        <Outlet />
      </FormProvider>
    </LanguageProvider>
  );
}
